import React from 'react'
import axios from 'axios'
import { useHistory } from 'react-router'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { Checkbox } from '@mui/material'
import {
  Calendar,
  Calendar1,
  Calendar2,
  Eye,
  People,
  Tag,
  TickCircle,
  User,
  UserOctagon,
  UserSquare
} from 'iconsax-react'

import SubTask from '../SubTask'
import TaskHeader from '../TaskHeader'
import NoCategoryTaskHeader from '../NoCategoryTaskHeader'
import LoadingSpinner from '@SonarCX/components/atoms/LoadingSpinner'
import Table from '@SonarCX/components/molecules/Table'
import Header from '@SonarCX/components/molecules/Table/Header'
import MobileDefault from '../Cells/MobileDefault'
import Title from '../Cells/Title'
import DueDateComponent from '../Cells/DueDateComponent'
import TagCell from '@SonarCX/components/molecules/Table/Cells/Tag'
import Status from '../Cells/Status'
import Type from '../Cells/Type'
import UpdatedAt from '../Cells/UpdatedAt'
import UsersList from '../Cells/UsersList'
import Priority from '../Cells/Priority'
import Category from '../Cells/Category'
import SingleUser from '../Cells/SingleUser'
import DateComponent from '@SonarCX/components/molecules/Table/Cells/Date'
import CustomField from '@SonarCX/components/molecules/Table/Cells/CustomFieldComponent'
import ClientFilter from '@SonarCX/pages/Tasks/Tabs/AllTasksTab/Filters/ClientFilter'
import DueDateFilter from '@SonarCX/pages/Tasks/Tabs/AllTasksTab/Filters/DueDateFilter'
import PriorityFilter from '@SonarCX/pages/Tasks/Tabs/AllTasksTab/Filters/PriorityFilter'
import OwnerFilter from '@SonarCX/pages/Tasks/Tabs/AllTasksTab/Filters/OwnerFilter'
import LeadFilter from '@SonarCX/pages/Tasks/Tabs/AllTasksTab/Filters/LeadFilter'
import AssigneeFilter from '@SonarCX/pages/Tasks/Tabs/AllTasksTab/Filters/AssigneeFilter'
import CategoryFilter from '@SonarCX/pages/Tasks/Tabs/AllTasksTab/Filters/CategoryFilter'
import StatusFilter from '@SonarCX/pages/Tasks/Tabs/AllTasksTab/Filters/StatusFilter'
import TypeFilter from '@SonarCX/pages/Tasks/Tabs/AllTasksTab/Filters/TypeFilter'
import CreatedDateFilter from '@SonarCX/pages/Tasks/Tabs/AllTasksTab/Filters/CreatedDateFilter'
import WatcherFilter from '@SonarCX/pages/Tasks/Tabs/AllTasksTab/Filters/WatcherFilter'

import { columnHelper } from './helpers'
import { Routes } from '@SonarCX/Routes'
import { getHeaders } from '@SonarCX/lib/api'
import { nullExpandArray } from '@SonarCX/utilities'
import { selectCustomFields } from '@SonarCX/slices/customFields'
import {
  selectAllTasksTable,
  removeMultipleSelected,
  addMultipleSelected
} from '@SonarCX/slices/allTasksTable'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { BLINK_DURATION } from '@SonarCX/components/molecules/Table/styles'
import useCombinedTableState from '@SonarCX/hooks/useCombinedTableState'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import { useTreeState, TYPES } from '@SonarCX/providers/TreeStateProvider'
import {
  CATEGORY_TAB,
  ALL_TAB,
  useTaskPageState
} from '@SonarCX/providers/TaskPageStateProvider'
import { useTaskFilters } from '@SonarCX/providers/TaskFiltersProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useTaskList } from '@SonarCX/providers/TaskListProvider'
import useFavorites from '@SonarCX/hooks/useFavorites'
import useTableDependencies from './useTableDependencies'
import useStyles from './styles'

const TABLE_PAGINATION_STATE_KEY = "lastPagination"

const TaskTable = ({
  allowDragDropSort,
  dragDropID,
  isInfinite = false,
  small,
  pageRef,
  multiple,
  setPageIndex,
  setPageSize,
  enablePagination,
  mutate,
  createTask,
  onPageChange: pageChanged,
  loadMore,
  isPaginatedInfiniteLoader = false,
  isLoadingMore,
  reachedEnd,
  isLoadingInitialData,
  totalData,
  headerProps = {},
  hideHeader,
  showNoCategoryHeader,
  onUserDetails,
  setTableSort,
  disableSort = false,
  pageNum,
  pageSize,
  disableSkeleton = false,
  initialSort = [],
  tableHeight,
  selectable = false,
  enableCustomContextMenu = false,
  id = 'task-table',
  customFieldsToInclude = '',
  ...props
}) => {
  const history = useHistory()
  const dispatch = useAppDispatch()

  const { customFields } = useAppSelector(selectCustomFields)
  const { selected } = useAppSelector(selectAllTasksTable)

  const {
    tasks: swrTasks,
    expanded,
    toggleExpanded,
    canAdd,
    columnOrder: order,
    columnVisible: visible,
    paginated,
    index,
    total,
    setPageSize: setUIPageSize,
    updateIndex,
    fullPagination
  } = useCombinedTableState()
  const {
    tasks: allTasks,
    loaded: loadedTasks,
    updatedTask,
    patchTasks
  } = useTaskAPI()
  const { tab, lastViewed, setLastViewed } = useTaskPageState()
  const { includeSubtasks, onlyFavorites } = useTaskFilters()
  const { favoriteTasks } = useFavorites()

  const { isSmall } = useBreakpoint()
  const { setIDs, addTasks, setMore, setLess, addTasksFront } = useTaskList()
  useTableDependencies()

  // Take the newest evailable tasks from both lists
  const tasks = React.useMemo(() => {
    const combined = []

    swrTasks.forEach(task => {
      const storedTask = allTasks[task.id]

      // If the task is not stored, add it
      if (!storedTask) {
        combined.push({ ...task })
        return
      }

      // If the task is stored but the stored task is older, add it
      if (new Date(storedTask.updated_at) < new Date(task.updated_at)) {
        combined.push({ ...task })
        return
      }
      // If its stored, just update the meta from server since we dont keep track of meta.
      // Since we have some legacy meta, we need to make sure its an array
      try {
        storedTask.meta = task.meta ? [...task.meta] : []
      } catch (err) {
        console.warn('Error parsing meta', err)
        storedTask.meta = []
      }

      // Otherwise, add the stored task
      combined.push({ ...storedTask })
    })

    if (!allowDragDropSort) {
      if (onlyFavorites) {
        return combined.filter(x => favoriteTasks.some(y => y.item_id === x.id))
      }

      return combined
    }

    return combined.sort((a, b) => a.sort_order - b.sort_order)
  }, [swrTasks, allTasks, allowDragDropSort, onlyFavorites, favoriteTasks])

  const {
    state: treeState,
    dispatch: treeDispatch,
    Actions: TreeActions
  } = useTreeState(TYPES.TASK)
  const lastViewedRef = React.useRef()
  const viewTimerRef = React.useRef()

  const smallMedia = React.useMemo(() => isSmall || small, [isSmall, small])

  const taskCfs = React.useMemo(
    () =>
      customFields.filter(
        x => x.object_type === 'task' && x.field_type !== 'table'
      ),
    [customFields]
  )

  const taskCustomFields = React.useMemo(
    () =>
      taskCfs.map(field =>
        columnHelper.accessor(field.id, {
          header: () => <Header>{field.title}</Header>,
          cell: ({ row }) => <CustomField row={row} customField={field} />,
          enableSorting: !disableSort
        })
      ),
    [taskCfs, disableSort]
  )

  const classes = useStyles({ adjust: smallMedia })

  const handleChangeFilter = React.useCallback(
    setter => e => {
      const val = e.target.value
      setter(val)
      treeDispatch({ type: TreeActions.CLEAR })
    },
    [treeDispatch, TreeActions.CLEAR]
  )

  const clearTimer = React.useCallback(() => {
    if (viewTimerRef.current) {
      clearTimeout(viewTimerRef.current)
    }
  }, [])

  const saveList = React.useCallback(
    async (id, tasks) => {
      const taskIDs = tasks.map(task => task.id)
      // Used to load the previous chunk of tasks
      setLess(() => {
        // Can't load previous if on the first page
        const isDone = page => {
          return page === 0
        }
        let { page, per_page, urlFunc } = fullPagination
        let done = isDone(page)
        // Enclosed function to fetch more and keep its state
        return async () => {
          if (done) {
            return
          }
          // Fetch and update the state
          const res = await axios.get(
            urlFunc(page - 1, per_page),
            await getHeaders()
          )
          const { data: newTasks, pagination } = res.data
          const { page: newPage } = pagination
          done = isDone(newPage)
          addTasksFront(newTasks.map(t => t.id))
        }
      })
      // Loads the next chunk of tasks
      setMore(() => {
        // Can't load more if on the last page
        const isDone = (page, per_page, total) => {
          const lastPageIdx = Math.ceil(total / per_page) - 1
          return page >= lastPageIdx
        }
        let { page, per_page, total, urlFunc } = fullPagination
        let done = isDone(page, per_page, total)
        // Enclosed function to fetch more and keep its state
        return async () => {
          if (done) {
            return
          }
          // Fetch and update the state
          const res = await axios.get(
            urlFunc(page + 1, per_page),
            await getHeaders()
          )
          const { data: newTasks, pagination } = res.data
          const { page: newPage } = pagination
          done = isDone(newPage, per_page, total)
          addTasks(newTasks.map(t => t.id))
        }
      })
      setIDs(taskIDs, id)
    },
    [setIDs, fullPagination, setMore, addTasks, setLess, addTasksFront]
  )

  const onMove = React.useCallback(
    (id, tasks) => {
      clearTimer()
      saveList(id, tasks)

      // Store pagination state for when user navigates back to this table
      window.localStorage.setItem(TABLE_PAGINATION_STATE_KEY, JSON.stringify({
        pageIndex: fullPagination?.page ?? 0,
        pageSize: fullPagination?.per_page ?? 25
      }))
      history.push(`${Routes.TASKS}/${id}`)
    },
    [clearTimer, saveList, history, fullPagination]
  )

  const handleClickRow = React.useCallback(
    row => {
      const id = row.original.id
      if (id !== 'temp') {
        onMove(id, tasks)
      }
    },
    [onMove, tasks]
  )

  const canExpandRow = React.useMemo(() => {
    return task => !(includeSubtasks && tab === ALL_TAB) && task.sub_tasks > 0
  }, [includeSubtasks, tab])

  const isExpanded = React.useMemo(() => {
    return includeSubtasks && tab === ALL_TAB ? {} : treeState
  }, [includeSubtasks, tab, treeState])

  const taskIDs = React.useMemo(() => tasks.map(task => task.id), [tasks])

  const isAllChecked = React.useMemo(
    () => taskIDs.length > 0 && taskIDs.every(id => selected.includes(id)),
    [selected, taskIDs]
  )

  const isSomeChecked = React.useMemo(
    () =>
      selected.length > 0 &&
      taskIDs.length > 0 &&
      !isAllChecked &&
      taskIDs.some(id => selected.includes(id)),
    [selected, isAllChecked, taskIDs]
  )

  const toggleSelectAll = React.useCallback(() => {
    if (isSomeChecked || isAllChecked) {
      dispatch(removeMultipleSelected(taskIDs))
      return
    }

    dispatch(addMultipleSelected(taskIDs))
  }, [isSomeChecked, isAllChecked, dispatch, taskIDs])

  const columns = React.useMemo(() => {
    if (smallMedia) {
      // only showing one column on small screens
      const titleColumn = columnHelper.accessor('title', {
        header: () => <Header>Tasks</Header>,
        enableSorting: !disableSort,
        cell: ({ row }) => <MobileDefault task={row.original} onMove={onMove} />
      })

      return [titleColumn]
    }

    // showing all columns
    const titleColumn = columnHelper.accessor('title', {
      header: () => (
        <Header>
          <Checkbox
            disableRipple
            checked={isAllChecked}
            indeterminate={isSomeChecked}
            onClick={e => {
              e.stopPropagation()
              toggleSelectAll()
            }}
            size='small'
            color='primary'
            className={classes.checkbox}
          />
          Tasks
        </Header>
      ),
      enableSorting: !disableSort,
      cell: ({ row }) => <Title task={row.original} onMove={onMove} />
    })

    const dueColumn = columnHelper.accessor('due_date', {
      header: () => <Header Icon={Calendar2}>Due</Header>,
      enableSorting: !disableSort,
      cell: ({ row }) => <DueDateComponent task={row.original} />,
      customFilter: props => <DueDateFilter menuMode {...props} />
    })

    const statusColumn = columnHelper.accessor('status', {
      header: () => <Header Icon={TickCircle}>Status</Header>,
      enableSorting: !disableSort,
      cell: ({ row }) => <Status task={row.original} />,
      customFilter: props => <StatusFilter menuMode {...props} />
    })

    const typeColumn = columnHelper.accessor('type', {
      header: () => <Header Icon={TickCircle}>Type</Header>,
      enableSorting: !disableSort,
      cell: ({ row }) => <Type task={row.original} />,
      customFilter: props => <TypeFilter menuMode {...props} />
    })

    const updatedAtColumn = onUserDetails
      ? null
      : columnHelper.accessor('updated_at', {
        header: () => <Header Icon={Calendar}>Updated</Header>,
        enableSorting: !disableSort,
        cell: ({ row }) => <UpdatedAt task={row.original} />
      })

    const assignColumn = onUserDetails
      ? null
      : columnHelper.accessor('assignee_ids', {
        header: () => <Header Icon={People}>Assignees</Header>,
        enableSorting: false,
        cell: ({ row }) => (
          <UsersList
            prop='assignee_ids'
            taskID={row.original.id}
            list={[...new Set([...(row.original.assignee_ids ?? [])])]}
            observerId={id}
          />
        ),
        customFilter: props => <AssigneeFilter menuMode {...props} />
      })

    const watcherColumn = onUserDetails
      ? null
      : columnHelper.accessor('watcher_ids', {
        header: () => <Header Icon={Eye}>Watchers</Header>,
        enableSorting: false,
        cell: ({ row }) => (
          <UsersList
            prop='watcher_ids'
            taskID={row.original.id}
            list={[...new Set([...(row.original.watcher_ids ?? [])])]}
            observerId={id}
          />
        ),
        customFilter: props => <WatcherFilter menuMode {...props} />
      })

    const priorityColumn = columnHelper.accessor('priority', {
      header: () => <Header Icon={TickCircle}>Priority</Header>,
      enableSorting: !disableSort,
      cell: ({ row }) => <Priority task={row.original} />,
      customFilter: props => <PriorityFilter menuMode {...props} />
    })

    const categoryColumn =
      tab === CATEGORY_TAB
        ? null
        : columnHelper.accessor('category_id', {
          header: () => <Header Icon={TickCircle}>Category</Header>,
          enableSorting: !disableSort,
          cell: ({ row }) => <Category task={row.original} />,
          customFilter: props => <CategoryFilter menuMode {...props} />
        })

    const clientColumn = columnHelper.accessor('concerning_user_id', {
      header: () => <Header Icon={User}>Client</Header>,
      enableSorting: !disableSort,
      cell: ({ row }) => (
        <SingleUser
          taskID={row.original.id}
          field='concerning_user_id'
          observerId={id}
          external
        />
      ),
      customFilter: props => <ClientFilter menuMode {...props} />
    })

    const authorColumn = columnHelper.accessor('created_by', {
      header: () => <Header Icon={User}>Author</Header>,
      enableSorting: !disableSort,
      cell: ({ row }) => (
        <SingleUser
          taskID={row.original.id}
          field='created_by'
          type='Author'
          disableEdit
          observerId={id}
        />
      )
    })

    const ownerColumn = columnHelper.accessor('owner_id', {
      header: () => <Header Icon={UserOctagon}>Owner</Header>,
      enableSorting: !disableSort,
      cell: ({ row }) => (
        <SingleUser
          taskID={row.original.id}
          field='owner_id'
          type='Owner'
          observerId={id}
        />
      ),
      customFilter: props => <OwnerFilter menuMode {...props} />
    })

    const taskLeadColumn = columnHelper.accessor('primary_assignee_id', {
      header: () => <Header Icon={UserSquare}>Task Lead</Header>,
      enableSorting: !disableSort,
      cell: ({ row }) => (
        <SingleUser
          taskID={row.original.id}
          field='primary_assignee_id'
          type='Task Lead'
          observerId={id}
        />
      ),
      customFilter: props => <LeadFilter menuMode {...props} />
    })

    const createdColumn = columnHelper.accessor('created_at', {
      header: () => <Header Icon={Calendar1}>Created</Header>,
      enableSorting: !disableSort,
      cell: ({ row }) => <DateComponent value={row.original.created_at} />,
      customFilter: props => <CreatedDateFilter menuMode {...props} />
    })

    const tagsColumn = columnHelper.accessor('tags', {
      header: () => <Header Icon={Tag}>Tags</Header>,
      enableSorting: !disableSort,
      cell: ({ row }) => <TagCell tags={row.original.tags} />
    })

    const allColumns = [
      titleColumn,
      dueColumn,
      statusColumn,
      typeColumn,
      updatedAtColumn,
      assignColumn,
      watcherColumn,
      priorityColumn,
      categoryColumn,
      clientColumn,
      authorColumn,
      createdColumn,
      ownerColumn,
      taskLeadColumn,
      tagsColumn,
      ...taskCustomFields
    ].filter(Boolean)

    const visibleColumns = allColumns.filter(col =>
      visible.includes(col.accessorKey)
    )

    return visibleColumns.sort((a, b) => {
      const aIndex = order.indexOf(a.accessorKey)
      const bIndex = order.indexOf(b.accessorKey)
      return aIndex - bIndex
    })
  }, [
    smallMedia,
    disableSort,
    onUserDetails,
    tab,
    taskCustomFields,
    onMove,
    id,
    visible,
    order,
    classes.checkbox,
    isAllChecked,
    isSomeChecked,
    toggleSelectAll
  ])

  // Row that gets rendered on expand
  const renderRowSubComponent = React.useCallback(
    row => (
      <SubTask
        id={row.original.id}
        row={row}
        targetRef={lastViewedRef}
        smallMedia={smallMedia}
        onMove={onMove}
        observerId={id}
        enableCustomContextMenu={enableCustomContextMenu}
        customFieldsToInclude={customFieldsToInclude}
      />
    ),
    [smallMedia, onMove, id, enableCustomContextMenu, customFieldsToInclude]
  )

  const handleCategoryExpand = React.useCallback(
    (id, open) =>
      treeDispatch({
        type: TreeActions.UPDATE,
        id,
        open
      }),
    [treeDispatch, TreeActions.UPDATE]
  )

  // Handles last viewed behavior
  React.useEffect(() => {
    // Ignore on all tasks since it's virtualized
    if (tab === ALL_TAB) {
      return
    }

    if (lastViewed && lastViewedRef.current && pageRef?.current) {
      // Scroll to last viewed
      const height = window.innerHeight
      let offset = 0
      if (multiple) {
        lastViewedRef.current.scrollIntoView({ block: 'center' })
        offset = pageRef.current.scrollTop + height / 6
      } else {
        const rect = lastViewedRef.current.getBoundingClientRect()
        offset = rect.y - height / 3
      }
      pageRef.current.scrollTop = Math.floor(offset)

      // Clear last viewed after animation ends
      clearTimer()
      viewTimerRef.current = setTimeout(() => {
        setLastViewed(null)
      }, BLINK_DURATION)
    }
  })

  React.useEffect(() => {
    if (hideHeader && !expanded) {
      toggleExpanded()
    }
  }, [hideHeader, expanded, toggleExpanded])

  const infiniteProps = React.useMemo(() => {
    if (!isPaginatedInfiniteLoader) {
      return {}
    }

    return {
      shouldRenderInfinitePagination: true,
      infintePaginationLoad: loadMore,
      isLoadingMore,
      reachedEnd
    }
  }, [isPaginatedInfiniteLoader, loadMore, isLoadingMore, reachedEnd])

  const handleSetPageSize = React.useCallback(
    size => {
      setPageSize(size)
      setUIPageSize(size)
    },
    [setPageSize, setUIPageSize]
  )

  const handlePageChange = React.useCallback(
    num => {
      setPageIndex(num)
      updateIndex(num)
      pageChanged && pageChanged(num)
    },
    [setPageIndex, updateIndex, pageChanged]
  )

  // Restore pagination state when navigating back to the table
  React.useEffect(() => {
    const lastPagination = window.localStorage.getItem(TABLE_PAGINATION_STATE_KEY)
    if (!lastPagination) {
      return
    }

    // Delay to ensure this happens after other state changes
    const timer = setTimeout(() => {
      window.localStorage.removeItem(TABLE_PAGINATION_STATE_KEY)
      const { pageIndex: lastPageIndex, pageSize: lastPageSize } = JSON.parse(lastPagination)
      handleSetPageSize(lastPageSize)
      handlePageChange(lastPageIndex)
    }, 250)

    return () => {
      clearTimeout(timer)
    }
  }, [handleSetPageSize, handlePageChange])

  const paginationProps = React.useMemo(() => {
    if (!enablePagination) {
      return { disablePagination: true }
    }

    return {
      onSetPageSize: handleSetPageSize,
      onPageChange: handlePageChange,
      showPagination: paginated,
      pageNum: index,
      numResults: total,
      manualPagination: true,
      loadedPages: 'all'
    }
  }, [
    enablePagination,
    handleSetPageSize,
    handlePageChange,
    paginated,
    index,
    total
  ])

  const handleDropSort = React.useCallback(
    (sourceID, targetID, after) => {
      // Add gaps to the array
      const expanded = nullExpandArray(tasks)

      // Find indices of both tasks in newly expanded array
      const sourceIdx = expanded.findIndex(t => t?.id === sourceID)
      const targetIdx = expanded.findIndex(t => t?.id === targetID)

      // Perform move
      expanded[targetIdx + (after ? 1 : -1)] = expanded[sourceIdx]
      expanded[sourceIdx] = null

      // Remove gaps after change
      const moved = expanded.filter(Boolean)

      // Assign new values for sort order
      const toUpdate = moved.map((task, i) => ({
        id: task.id,
        sort_order: i * 10
      }))

      // Update in api
      patchTasks(toUpdate)
        .then(() => dispatch(AddSuccessAlert('Changes saved', 1500)))
        .catch(err => dispatch(AddErrorAlert({ err })))
    },
    [tasks, patchTasks, dispatch]
  )

  return (
    <div className={classes.root}>
      {!hideHeader && (
        <TaskHeader
          handleChangeFilter={handleChangeFilter}
          fakePagination={!enablePagination}
          isInfinite={isInfinite}
          totalData={totalData}
          canAdd={canAdd}
          createTask={createTask}
          {...headerProps}
          loaded={props.loaded}
        />
      )}

      {showNoCategoryHeader && (
        <NoCategoryTaskHeader
          handleChangeFilter={handleChangeFilter}
          fakePagination={!enablePagination}
          isInfinite={isInfinite}
          totalData={totalData}
          canAdd={canAdd}
          createTask={createTask}
          {...headerProps}
        />
      )}

      {expanded &&
        (isLoadingInitialData || !loadedTasks ? (
          <LoadingSpinner />
        ) : (
          <Table
            id={id}
            data={tasks}
            columns={columns}
            onRowClicked={handleClickRow}
            message='There are no tasks in this category'
            // Task specific
            renderRowSubComponent={renderRowSubComponent}
            canExpandRow={canExpandRow}
            expanded={isExpanded}
            onToggleExpanded={handleCategoryExpand}
            target={updatedTask}
            // Last seen
            targetID={lastViewed}
            targetRef={lastViewedRef}
            // UI
            hideHeader={smallMedia}
            isSmall={smallMedia}
            padBottom={smallMedia}
            disableHeader={smallMedia}
            hideCount
            // Extra
            {...infiniteProps}
            {...paginationProps}
            {...props}
            // Sorting
            enableSort
            useManualSort={true}
            setTableSort={setTableSort}
            disableSortRemove={true}
            pageNum={pageNum && pageNum}
            disableSkeleton
            allowDragDropSort={allowDragDropSort}
            onDragDrop={handleDropSort}
            dragDropID={dragDropID}
            initialSort={initialSort}
            enableResizing={!isSmall}
            enableCustomContextMenu={enableCustomContextMenu}
            {...(tab === ALL_TAB
              ? {
                stickyHeader: true,
                tableHeight: tableHeight ?? '72svh',
                paginationOptions: [25, 50]
              }
              : {})}
          />
        ))}
    </div>
  )
}

export default TaskTable
